import apiClient from '@src/api/clients';

const BASE_URL = 'tasks/tasks/';
const ASSIGNEES_URL = 'tasks/assignees/';
const ASSIGNMENT_USERS_URL = 'tasks/assignment-users/';
const ASSIGNMENT_RULES_URL = 'tasks/assignment-rules/';

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );

export const listTasks = (params = {}) =>
  apiClient.request({ url: BASE_URL, method: 'GET', payload: cleanParams(params) });

export const retrieveTask = (taskId) =>
  apiClient.request({ url: `${BASE_URL}${taskId}/`, method: 'GET' });

export const createTask = (payload) =>
  apiClient.request({ url: BASE_URL, method: 'POST', payload });

export const updateTask = (taskId, payload) =>
  apiClient.request({ url: `${BASE_URL}${taskId}/`, method: 'PATCH', payload });

export const deleteTask = (taskId) =>
  apiClient.request({ url: `${BASE_URL}${taskId}/`, method: 'DELETE' });

export const bulkDeleteTasks = (ids) =>
  apiClient.request({ url: `${BASE_URL}bulk-delete/`, method: 'POST', payload: { ids } });

export const bulkCompleteTasks = (ids) =>
  apiClient.request({ url: `${BASE_URL}bulk-complete/`, method: 'POST', payload: { ids } });

// Parse a free-text / voice-dictated task into a structured Task via Sonnet.
// Pass create: false to preview without creating.
export const parseNaturalTask = ({ transcript, create = true }) =>
  apiClient.request({
    url: `${BASE_URL}parse-natural/`,
    method: 'POST',
    payload: { transcript, create },
  });

export const snoozeTask = (taskId, until) =>
  apiClient.request({ url: `${BASE_URL}${taskId}/snooze/`, method: 'POST', payload: { until } });

export const approveTaskProposal = (taskId, payload = {}) =>
  apiClient.request({ url: `${BASE_URL}${taskId}/approve/`, method: 'POST', payload });

export const cancelTaskSend = (taskId) =>
  apiClient.request({ url: `${BASE_URL}${taskId}/cancel-send/`, method: 'POST' });

export const rejectTaskProposal = (taskId, reason = '') =>
  apiClient.request({ url: `${BASE_URL}${taskId}/reject/`, method: 'POST', payload: { reason } });

export const regenerateTaskReply = (taskId, additionalContext = '') =>
  apiClient.request({
    url: `${BASE_URL}${taskId}/regenerate-reply/`,
    method: 'POST',
    payload: { additional_context: additionalContext },
  });

export const polishTaskReply = (taskId, rawText) =>
  apiClient.request({
    url: `${BASE_URL}${taskId}/polish-reply/`,
    method: 'POST',
    payload: { raw_text: rawText },
  });

export const fetchTaskActivity = (taskId) =>
  apiClient.request({ url: `${BASE_URL}${taskId}/activity/`, method: 'GET' });

export const splitTaskIntoSubtasks = (taskId) =>
  apiClient.request({ url: `${BASE_URL}${taskId}/split-into-subtasks/`, method: 'POST' });

export const executeTaskDomain = (taskId) =>
  apiClient.request({ url: `${BASE_URL}${taskId}/execute-domain/`, method: 'POST' });

export const fetchTasksSummary = (params = {}) =>
  apiClient.request({ url: `${BASE_URL}summary/`, method: 'GET', payload: cleanParams(params) });

export const fetchTasksSourceCounts = () =>
  apiClient.request({ url: `${BASE_URL}source-counts/`, method: 'GET' });

// Returns an MP3 Blob of the AI-generated triage summary narration.
// Caller is responsible for URL.createObjectURL(blob) + cleanup.
export const fetchTasksSummaryAudio = () =>
  apiClient.request({
    url: `${BASE_URL}summary/audio/`,
    method: 'GET',
    options: { responseType: 'blob' },
  });

export const listAssignees = (params = {}) =>
  apiClient.request({ url: ASSIGNEES_URL, method: 'GET', payload: cleanParams(params) });

export const listAssignmentUsers = (params = {}) =>
  apiClient.request({ url: ASSIGNMENT_USERS_URL, method: 'GET', payload: cleanParams(params) });

export const updateAssignmentUser = (userId, payload) =>
  apiClient.request({ url: `${ASSIGNMENT_USERS_URL}${userId}/`, method: 'PATCH', payload });

export const listAssignmentRules = (params = {}) =>
  apiClient.request({ url: ASSIGNMENT_RULES_URL, method: 'GET', payload: cleanParams(params) });

export const createAssignmentRule = (payload) =>
  apiClient.request({ url: ASSIGNMENT_RULES_URL, method: 'POST', payload });

export const updateAssignmentRule = (ruleId, payload) =>
  apiClient.request({ url: `${ASSIGNMENT_RULES_URL}${ruleId}/`, method: 'PATCH', payload });

export const deleteAssignmentRule = (ruleId) =>
  apiClient.request({ url: `${ASSIGNMENT_RULES_URL}${ruleId}/`, method: 'DELETE' });

export default {
  listTasks,
  retrieveTask,
  createTask,
  updateTask,
  deleteTask,
  bulkDeleteTasks,
  bulkCompleteTasks,
  parseNaturalTask,
  snoozeTask,
  approveTaskProposal,
  cancelTaskSend,
  rejectTaskProposal,
  regenerateTaskReply,
  polishTaskReply,
  fetchTaskActivity,
  splitTaskIntoSubtasks,
  executeTaskDomain,
  fetchTasksSummary,
  fetchTasksSummaryAudio,
  fetchTasksSourceCounts,
  listAssignees,
  listAssignmentUsers,
  updateAssignmentUser,
  listAssignmentRules,
  createAssignmentRule,
  updateAssignmentRule,
  deleteAssignmentRule,
};
